/**
 * useTags / mutation hooks — local-first (SQLite WASM)
 *
 * Reads use useSQLiteQuery for reactive SQLite subscriptions.
 * Writes go to SQLite immediately, then enqueue for the sync engine.
 */

import { useState } from 'react'
import { useFingerprintedQuery } from '../lib/reactive'
import {
  updateTag,
  updateNote,
  deleteTagLocal,
  enqueueSyncOp,
  getAllTags,
  getTag,
  getNote,
  type LocalTag,
} from '../lib/db'
import { query } from '../lib/sqlite'
import {
  renameHashtagInContent,
  addHashtagToContent,
  removeHashtagFromContent,
  extractHashtags,
} from '../lib/hashtags'
import { normalizeTagName } from '@caliu/utils'
import { tagFullPath } from '../lib/tagHierarchy'
import { track, AnalyticsEvent } from '../lib/analytics'
import { extractTitleFromContent } from './useEditorAutosave'
import { useSyncNoteTags } from './useNotes'
import { isSharedWithMe } from '../lib/sharing'
import { memberTextLocked } from '../lib/noteEngineState'
import { useAuthStore } from '../store/auth'
import type { Tag } from '../types'

/**
 * Whether a tag rename rewrites this note's hashtags. Never an encrypted note
 * (ciphertext), and never a note shared with this account: its text is the
 * owner's, editor or viewer alike (a rename is this account's tidying).
 */
export function tagRenameRewrites(
  row: { is_encrypted: number; owner_id: string | null },
  meId: string | null | undefined,
): boolean {
  if (row.is_encrypted) return false
  return !isSharedWithMe({ ownerId: row.owner_id ?? undefined }, meId)
}

// ── useTags ───────────────────────────────────────────────────────────────

/** Reactive tag list with fingerprint — only re-renders when tags actually
 *  change (added, removed, renamed, repinned, restyled), not when the
 *  note_tags junction changes. */
export function useTags(): LocalTag[] {
  return useFingerprintedQuery<LocalTag[]>({
    tables: ['tags'],
    fingerprint: () => {
      const rows = query<{ id: string; name: string; parent_id: string | null; color: string | null; icon: string | null; is_pinned: number }>(
        'SELECT id, name, parent_id, color, icon, is_pinned FROM tags ORDER BY name',
      )
      return rows.map((r) => `${r.id}:${r.name}:${r.parent_id}:${r.color}:${r.icon}:${r.is_pinned}`).join('|')
    },
    query: () => getAllTags(),
  })
}

// ── Helpers ───────────────────────────────────────────────────────────────

/** Pinned tags first, then alphabetical — applied per tree level. */
export function compareTagsPinnedFirst(a: Tag, b: Tag): number {
  if (Boolean(a.isPinned) !== Boolean(b.isPinned)) return a.isPinned ? -1 : 1
  return a.name.localeCompare(b.name)
}

/** Ordering for a flat list of tag search results (command palette): pinned
 *  first, then alphabetical on the FULL handle. `compareTagsPinnedFirst` sorts
 *  on the leaf `name`, which is only correct inside one tree level — flattened,
 *  it interleaves "work/alpha" with "zeta/alpha". Mirrored in Swift's
 *  `CommandPaletteController.rankedTags`. */
export function compareTagHandlesPinnedFirst(
  a: { tag: Tag; handle: string },
  b: { tag: Tag; handle: string },
): number {
  if (Boolean(a.tag.isPinned) !== Boolean(b.tag.isPinned)) return a.tag.isPinned ? -1 : 1
  return a.handle.localeCompare(b.handle)
}

/**
 * Full mutable state for a tag `update` sync op. The sync queue dedup keeps
 * only the latest update op per entity, so every tag update op must carry
 * the complete state — otherwise a pin toggle superseded by a later rename
 * (or vice versa) would silently lose one of the changes.
 */
function tagSyncOpData(tag: LocalTag): Record<string, unknown> {
  return {
    name: tag.name,
    color: tag.color ?? null,
    icon: tag.icon ?? null,
    is_pinned: tag.isPinned ?? false,
  }
}

// ── useUpdateTag ──────────────────────────────────────────────────────────

export function useUpdateTag() {
  const [isPending, setIsPending] = useState(false)
  const mutate = async ({ id, ...data }: { id: string; name?: string; color?: string | null; icon?: string | null; isPinned?: boolean }) => {
    setIsPending(true)
    try {
      updateTag(id, { ...data, syncStatus: 'pending' } as Partial<LocalTag>)
      const updated = getTag(id)
      enqueueSyncOp({
        type: 'update',
        entity: 'tag',
        entityId: id,
        data: updated ? tagSyncOpData(updated) : data,
        timestamp: Date.now(),
        retries: 0,
      })
      // A pin-only update (usePinTag routes through here) reads as pin/unpin;
      // anything else is an appearance change (color / icon / name).
      const isPinOnly =
        data.isPinned !== undefined &&
        data.name === undefined &&
        data.color === undefined &&
        data.icon === undefined
      track(AnalyticsEvent.TagAction, {
        action: isPinOnly ? (data.isPinned ? 'pin' : 'unpin') : 'edit_appearance',
        source: 'sidebar',
      })
    } finally {
      setIsPending(false)
    }
  }
  return { mutate, isPending }
}

// ── usePinTag ─────────────────────────────────────────────────────────────

/** Pin/unpin a tag — mirrors usePinNote: update-backed, no dedicated op. */
export function usePinTag() {
  const updateTagMutation = useUpdateTag()
  const mutate = async ({ id, isPinned }: { id: string; isPinned: boolean }) => {
    await updateTagMutation.mutate({ id, isPinned })
  }
  return { mutate, isPending: updateTagMutation.isPending }
}

// ── useRenameTag ──────────────────────────────────────────────────────────

export type RenameTagResult =
  | { ok: true }
  | { ok: false; error: 'invalid' | 'conflict' }

/**
 * Valid tag name segment: the shared hashtag charset (Unicode letters, marks,
 * digits, `_`, `-`) plus spaces (multi-word tags), minus the hierarchy
 * separator. Runs of spaces are collapsed before validation.
 */
const TAG_NAME_REGEX = /^[\p{L}\p{M}\p{N}_ -]+$/u

/**
 * Bulk tag rename. Renames the tag row AND rewrites the `#hashtag` in the
 * content of every note that carries it — including archived and trashed
 * notes (trash strips note_tags but keeps content, so we scan content, not
 * the junction table). Descendant handles (`#old/child`) are rewritten too.
 * Encrypted notes are skipped (their content is ciphertext).
 */
export function useRenameTag() {
  const [isPending, setIsPending] = useState(false)

  const mutate = async ({ id, name }: { id: string; name: string }): Promise<RenameTagResult> => {
    const newName = normalizeTagName(name)
    if (!newName || !TAG_NAME_REGEX.test(newName)) return { ok: false, error: 'invalid' }

    const tag = getTag(id)
    if (!tag) return { ok: false, error: 'invalid' }
    if (newName === tag.name) return { ok: true }

    const allTags = getAllTags()
    const siblingConflict = allTags.some(
      (t) =>
        t.id !== id &&
        (t.parentId ?? null) === (tag.parentId ?? null) &&
        t.name.toLowerCase() === newName,
    )
    if (siblingConflict) return { ok: false, error: 'conflict' }

    const byId = new Map(allTags.map((t) => [t.id, t]))
    const oldHandle = tagFullPath(tag, byId)
    const parent = tag.parentId ? byId.get(tag.parentId) : undefined
    const newHandle = parent ? `${tagFullPath(parent, byId)}/${newName}` : newName

    setIsPending(true)
    try {
      // Persist any pending 800ms autosave of the open note BEFORE reading
      // note content from SQLite — same guard the history restore flow uses.
      window.dispatchEvent(new Event('caliu-flush-autosave'))

      // 1. Rename the tag row. Child tags keep their own name segment; their
      //    handles shift implicitly via the parent chain.
      updateTag(id, { name: newName, syncStatus: 'pending' })
      const updated = getTag(id)
      enqueueSyncOp({
        type: 'update',
        entity: 'tag',
        entityId: id,
        data: updated ? tagSyncOpData(updated) : { name: newName },
        timestamp: Date.now(),
        retries: 0,
      })

      // 2. Rewrite the hashtag in every note that carries it.
      const rows = query<{
        id: string
        content: string | null
        updated_at: string
        is_encrypted: number
        owner_id: string | null
      }>('SELECT id, content, updated_at, is_encrypted, owner_id FROM notes')
      const meId = useAuthStore.getState().user?.id
      for (const row of rows) {
        if (!tagRenameRewrites(row, meId)) continue
        const content = row.content ?? ''
        const newContent = renameHashtagInContent(content, oldHandle, newHandle)
        if (newContent === content) continue

        // A tag rename is a STRUCTURAL edit, not a content edit, so preserve
        // each note's updated_at (sent as both updated_at and
        // expected_updated_at). This keeps the rename from floating notes to
        // the top of an updated_at-sorted list, matching the Swift client; the
        // worker honors the client updated_at for content updates.
        updateNote(row.id, {
          content: newContent,
          title: extractTitleFromContent(newContent),
          wordCount: newContent.split(/\s+/).filter(Boolean).length,
          updatedAt: row.updated_at,
          syncStatus: 'pending',
        })
        enqueueSyncOp({
          type: 'update',
          entity: 'note',
          entityId: row.id,
          data: {
            content: newContent,
            updated_at: row.updated_at,
            expected_updated_at: row.updated_at,
          },
          timestamp: Date.now(),
          retries: 0,
        })
        // If this note is open in the editor, make it re-read from SQLite
        // instead of stomping the rewrite with its in-memory text.
        window.dispatchEvent(
          new CustomEvent('caliu-refresh-active-note', { detail: { noteId: row.id } }),
        )
      }

      track(AnalyticsEvent.TagAction, { action: 'rename', source: 'sidebar' })
      return { ok: true }
    } finally {
      setIsPending(false)
    }
  }

  return { mutate, isPending }
}

// ── useAddTagToNote ───────────────────────────────────────────────────────

/**
 * Add a tag to a note by writing the `#hashtag` into its markdown — the
 * smart way: appended to the note's dedicated tag line when one exists,
 * otherwise at the end of the note (see addHashtagToContent). The note_tags
 * junction updates immediately via the same tag-sync path autosave uses.
 */
export function useAddTagToNote() {
  const syncNoteTags = useSyncNoteTags()
  const [isPending, setIsPending] = useState(false)

  const mutate = async ({ noteId, handle }: { noteId: string; handle: string }) => {
    setIsPending(true)
    try {
      // Persist any pending 800ms autosave before reading from SQLite.
      window.dispatchEvent(new Event('caliu-flush-autosave'))

      const note = getNote(noteId)
      // Encrypted note content is ciphertext — never rewrite it. A viewer
      // never edits someone else's text (PLAN.md D5).
      if (!note || note.isEncrypted || memberTextLocked(note.role)) return
      const content = note.content ?? ''
      const newContent = addHashtagToContent(content, handle)
      if (newContent === content) return

      const now = new Date().toISOString()
      updateNote(noteId, {
        content: newContent,
        title: extractTitleFromContent(newContent),
        wordCount: newContent.split(/\s+/).filter(Boolean).length,
        updatedAt: now,
        syncStatus: 'pending',
      })
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: noteId,
        data: { content: newContent, updated_at: now, expected_updated_at: note.updatedAt },
        timestamp: Date.now(),
        retries: 0,
      })
      // If this note is open in the editor, make it re-read from SQLite.
      window.dispatchEvent(
        new CustomEvent('caliu-refresh-active-note', { detail: { noteId } }),
      )

      await syncNoteTags.mutate({
        noteId,
        tags: extractHashtags(newContent),
        source: 'note_actions_menu',
      })
      track(AnalyticsEvent.TagAction, { action: 'add_to_note', source: 'note_actions_menu' })
    } finally {
      setIsPending(false)
    }
  }

  return { mutate, isPending }
}

// ── useRemoveTagFromNote ──────────────────────────────────────────────────

/**
 * Remove a tag from a note by stripping its `#hashtag` from the note's
 * markdown (see removeHashtagFromContent) — the inverse of useAddTagToNote.
 * The note_tags junction updates immediately via the same tag-sync path
 * autosave uses.
 */
export function useRemoveTagFromNote() {
  const syncNoteTags = useSyncNoteTags()
  const [isPending, setIsPending] = useState(false)

  const mutate = async ({ noteId, handle }: { noteId: string; handle: string }) => {
    setIsPending(true)
    try {
      // Persist any pending 800ms autosave before reading from SQLite.
      window.dispatchEvent(new Event('caliu-flush-autosave'))

      const note = getNote(noteId)
      // Encrypted note content is ciphertext — never rewrite it. A viewer
      // never edits someone else's text (PLAN.md D5).
      if (!note || note.isEncrypted || memberTextLocked(note.role)) return
      const content = note.content ?? ''
      const newContent = removeHashtagFromContent(content, handle)
      if (newContent === content) return

      const now = new Date().toISOString()
      updateNote(noteId, {
        content: newContent,
        title: extractTitleFromContent(newContent),
        wordCount: newContent.split(/\s+/).filter(Boolean).length,
        updatedAt: now,
        syncStatus: 'pending',
      })
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: noteId,
        data: { content: newContent, updated_at: now, expected_updated_at: note.updatedAt },
        timestamp: Date.now(),
        retries: 0,
      })
      // If this note is open in the editor, make it re-read from SQLite.
      window.dispatchEvent(
        new CustomEvent('caliu-refresh-active-note', { detail: { noteId } }),
      )

      await syncNoteTags.mutate({
        noteId,
        tags: extractHashtags(newContent),
        source: 'note_actions_menu',
      })
      track(AnalyticsEvent.TagAction, {
        action: 'remove_from_note',
        source: 'note_actions_menu',
      })
    } finally {
      setIsPending(false)
    }
  }

  return { mutate, isPending }
}

// ── useDeleteTag ──────────────────────────────────────────────────────────

export function useDeleteTag() {
  const [isPending, setIsPending] = useState(false)
  const mutate = async (id: string) => {
    setIsPending(true)
    try {
      deleteTagLocal(id)
      enqueueSyncOp({ type: 'delete', entity: 'tag', entityId: id, timestamp: Date.now(), retries: 0 })
      track(AnalyticsEvent.TagAction, { action: 'delete', source: 'sidebar' })
    } finally {
      setIsPending(false)
    }
  }
  return { mutate, isPending }
}
